import React, { useState, useEffect } from "react";
import { Add } from "@mui/icons-material"
import { Box, Card, CardHeader, CardContent, Typography, Avatar, Button, Skeleton, CardActions } from "@mui/material"
import styled from "@emotion/styled"
import UserAssignmentDialog from "./UserAssignmentDialog";
import { useSelector } from "react-redux";
import { useHasPermission } from "../utils/permissionUtils";
import { getAvatarUrl } from "../utils/avatarUtils";
import { useIsProjectLocked } from "../utils/lockUtils";

const InfoCard = styled(Card)(({ theme }) => ({
    gridColumn: 'span 3',
    [theme.breakpoints.up('sm')]: {
        gridColumn: 'span 1',
    },
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
}));

const UserBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(1, 1, 2),
    gap: theme.spacing(1),
}));

const CardTitleText = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.h5.fontSize,
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
    flexGrow: 1,
    overflowY: 'auto',
}));

const SectionLabel = styled(Typography)({
    display: "block",
});

const CreatorSection = styled(Box)(({ theme }) => ({
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 2, 0),
    flexShrink: 0,
}));

const WideButton = styled(Button)(({ theme }) => ({
    width: '100%',
    alignItems: 'center',
}));

const ProjectPeople = () => {
    const project = useSelector(state => state.project.currentProject);
    const [responsible, setResponsible] = useState(null)
    const [assignees, setAssignees] = useState([])
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const creator = project?.creator;
    const isProjectLoaded = Boolean(project?._id);
    const hasPermission = useHasPermission();
    const canAssignProject = hasPermission('assignProject');
    const isLocked = useIsProjectLocked(project);

    const handleOpenDialog = () => {
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
    };

    useEffect(() => {
        setResponsible(project?.responsible);
    }, [project?.responsible]);

    useEffect(() => {
        setAssignees(project?.assignees || []);
    }, [project?.assignees]);

    return (
        <InfoCard>
            <CardHeader title={<CardTitleText>Team Members</CardTitleText>} />
            <StyledCardContent>
                <SectionLabel variant="button">Project Lead</SectionLabel>
                <UserBox>
                    {(responsible !== undefined && responsible !== null) ?
                        <>
                            <Avatar src={getAvatarUrl(responsible)} alt={responsible?.fullName} />
                            <Box>
                                <Typography variant="subtitle2">{responsible.fullName}</Typography>
                                <Typography variant="body2">{responsible.title}</Typography>
                            </Box>
                        </>
                        : (responsible === undefined) ?
                            <>
                                <Skeleton variant="circular" width={40} height={40} />
                                <Box>
                                    <Skeleton width="160px" />
                                    <Skeleton width="60%" />
                                </Box>
                            </>
                            :
                            <>
                                <Typography variant="body2">No Assigned Lead yet</Typography>
                            </>
                    }
                </UserBox>
                <SectionLabel variant="button">Assigned Members</SectionLabel>
                {!isProjectLoaded ? (
                    <UserBox>
                        <Skeleton variant="circular" width={40} height={40} />
                        <Box>
                            <Skeleton width="160px" />
                            <Skeleton width="60%" />
                        </Box>
                    </UserBox>
                ) : assignees.length > 0 ? (
                    assignees.map((assignee) => (
                        <UserBox key={assignee._id}>
                            <Avatar src={getAvatarUrl(assignee)} alt={assignee.fullName} />
                            <Box>
                                <Typography variant="subtitle2">{assignee.fullName}</Typography>
                                <Typography variant="body2">{assignee.title}</Typography>
                            </Box>
                        </UserBox>
                    ))
                ) : (
                    <UserBox>
                        <Typography variant="body2">No Assigned Members yet</Typography>
                    </UserBox>
                )}
            </StyledCardContent>
            <CreatorSection>
                <SectionLabel variant="button">Created By</SectionLabel>
                <UserBox>
                    {!isProjectLoaded ?
                        <>
                            <Skeleton variant="circular" width={40} height={40} />
                            <Box>
                                <Skeleton width="160px" />
                                <Skeleton width="60%" />
                            </Box>
                        </>
                        : creator ?
                            <>
                                <Avatar src={getAvatarUrl(creator)} alt={creator.fullName} />
                                <Box>
                                    <Typography variant="subtitle2">{creator.fullName}</Typography>
                                    <Typography variant="body2">{creator.title}</Typography>
                                </Box>
                            </>
                            :
                            <Typography variant="body2">Not recorded</Typography>
                    }
                </UserBox>
            </CreatorSection>
            {canAssignProject &&
                <CardActions>
                    <WideButton variant="outlined" onClick={handleOpenDialog} disabled={isLocked}>
                        <Add /> Assign New Member
                    </WideButton>
                </CardActions>
            }
            <UserAssignmentDialog
                isOpen={isDialogOpen}
                onClose={handleCloseDialog}
                objectId={project?._id}
                objectType="project"
                accountId={project?.account?._id}
            />
        </InfoCard>
    );
}

export default ProjectPeople;